/**
 * Subscription Admin API
 * Admin endpoints for managing user subscriptions
 */

import { baseApiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export type SubscriptionStatus = 'none' | 'trial' | 'active' | 'expired' | 'cancelled';

export interface UserWithSubscription {
  user_id: string;
  email: string | null;
  username: string | null;
  display_name: string | null;
  subscription_status: SubscriptionStatus;
  product_id: string | null;
  trial_end_at: string | null;
  subscription_end_at: string | null;
  will_renew: boolean;
  store: string | null;
  payment_channel: string | null;
  created_at: string | null;
}

export interface UserSubscriptionListResponse {
  users: UserWithSubscription[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface UserSubscriptionSchema {
  status: SubscriptionStatus;
  role_level: 'free' | 'pro' | 'business' | 'max';
  entitlement_id: string;
  product_id: string | null;
  is_trial: boolean;
  trial_end_at: string | null;
  subscription_end_at: string | null;
  will_renew: boolean;
  features: string[];
  payment_channel: string | null;
  store: string | null;
  auto_renew_status: boolean;
  subscription_start_at: string | null;
  original_purchase_date: string | null;
  latest_purchase_date: string | null;
  external_transaction_id: string | null;
  original_external_transaction_id: string | null;
}

export interface RecentEvent {
  user_id: string;
  display_name: string | null;
  event_type: string;
  event_source: string | null;
  previous_status: string | null;
  new_status: string | null;
  product_id: string | null;
  created_at: string;
}

export interface UserSubscriptionDetail {
  user_id: string;
  email: string | null;
  username: string | null;
  display_name: string | null;
  avatar: string | null;
  subscription: UserSubscriptionSchema | null;
  events: RecentEvent[];
}

export interface AdminSetUserSubscriptionRequest {
  status: SubscriptionStatus;
  product_id?: string | null;
  subscription_end_at?: string | null;
  reason: string;
}

export interface ListUsersParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: SubscriptionStatus[];
  product_id?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// ============================================================================
// API Functions
// ============================================================================

export const subscriptionApi = {
  /**
   * List users with subscription details
   */
  listUsers: async (params: ListUsersParams = {}): Promise<UserSubscriptionListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.page_size) queryParams.set('page_size', params.page_size.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.status?.length) {
      params.status.forEach(s => queryParams.append('status', s));
    }
    if (params.product_id) queryParams.set('product_id', params.product_id);
    if (params.sort_by) queryParams.set('sort_by', params.sort_by);
    if (params.sort_order) queryParams.set('sort_order', params.sort_order);

    const response = await baseApiClient.get(`/v1/subscription/admin/users?${queryParams}`);
    return response.data;
  },

  /**
   * Get detailed user subscription info
   */
  getUserDetail: async (userId: string): Promise<UserSubscriptionDetail> => {
    const response = await baseApiClient.get(`/v1/subscription/admin/users/${userId}/detail`);
    return response.data;
  },

  /**
   * Admin set user subscription status
   */
  setUserSubscription: async (
    userId: string,
    request: AdminSetUserSubscriptionRequest
  ): Promise<UserSubscriptionSchema> => {
    const response = await baseApiClient.put(
      `/v1/subscription/admin/users/${userId}`,
      request
    );
    return response.data;
  },

  /**
   * Reset user subscription to none (convenience method)
   */
  resetUserSubscription: async (userId: string, reason: string): Promise<UserSubscriptionSchema> => {
    return subscriptionApi.setUserSubscription(userId, {
      status: 'none',
      product_id: null,
      subscription_end_at: null,
      reason,
    });
  },

  /**
   * Grant trial to user
   */
  grantTrial: async (userId: string, days: number, reason: string): Promise<UserSubscriptionSchema> => {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + days);

    return subscriptionApi.setUserSubscription(userId, {
      status: 'trial',
      subscription_end_at: trialEnd.toISOString(),
      reason,
    });
  },

  /**
   * Grant active subscription to user
   */
  grantSubscription: async (
    userId: string,
    productId: string,
    daysOrEndDate: number | Date,
    reason: string
  ): Promise<UserSubscriptionSchema> => {
    let endDate: Date;
    if (typeof daysOrEndDate === 'number') {
      endDate = new Date();
      endDate.setDate(endDate.getDate() + daysOrEndDate);
    } else {
      endDate = daysOrEndDate;
    }

    return subscriptionApi.setUserSubscription(userId, {
      status: 'active',
      product_id: productId,
      subscription_end_at: endDate.toISOString(),
      reason,
    });
  },
};
